import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Star, Zap, Crown, Truck, Clock, Bot, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const PLAN_STYLES = {
  basic: {
    border: 'border-slate-200',
    accent: 'text-slate-800',
    priceColor: 'text-slate-800',
    btn: 'bg-slate-700 hover:bg-slate-800',
    icon: Star,
    iconColor: 'text-slate-600',
    setupBg: 'bg-slate-100 text-slate-700',
  },
  pro: {
    border: 'border-blue-400 border-2 shadow-blue-500/10 shadow-lg',
    accent: 'text-blue-600',
    priceColor: 'text-blue-600',
    btn: 'bg-blue-600 hover:bg-blue-700',
    icon: Zap,
    iconColor: 'text-blue-600',
    setupBg: 'bg-blue-100 text-blue-700',
    badge: 'MOST POPULAR',
    badgeBg: 'bg-blue-600',
  },
  extreme: {
    border: 'border-purple-400 border-2 shadow-purple-500/10 shadow-lg',
    accent: 'text-purple-600',
    priceColor: 'text-purple-600',
    btn: 'bg-purple-600 hover:bg-purple-700',
    icon: Crown,
    iconColor: 'text-purple-600',
    setupBg: 'bg-purple-100 text-purple-700',
  },
};

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/pricing/plans')
      .then(res => setPlans(res.data.plans))
      .catch(() => toast.error('Failed to load pricing'));
  }, []);

  if (!plans) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const planOrder = ['basic', 'pro', 'extreme'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="pricing-page">
      {/* Nav */}
      <nav className="border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-white tracking-tight">Urbanvision Logistics</span>
          </div>
          <a href="/auth" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="login-link">
            Already a member? <span className="text-blue-400 font-medium">Log In</span>
          </a>
        </div>
      </nav>

      <div className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Simple, Transparent Pricing</h1>
            <p className="text-base sm:text-lg text-slate-300">Choose the plan that fits your brokerage. Every plan includes your own franchise master code.</p>
          </div>

          {/* Phase comparison strip */}
          <div className="mb-8" data-testid="phase-comparison-strip">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 backdrop-blur">
              <p className="text-center text-xs uppercase tracking-widest text-slate-400 mb-3">
                AI Dispatch Phases — Included at Each Tier
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/40">
                  <div className="bg-blue-500/20 text-blue-300 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <p className="text-sm font-semibold text-white">Phase 1 — Smart Suggest</p>
                    <p className="text-xs text-slate-400 mt-0.5">AI recommends best loads. You click to book.</p>
                    <p className="text-[10px] text-blue-300 mt-1 font-medium">Included: Basic, Pro, Extreme</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/40">
                  <div className="bg-purple-500/20 text-purple-300 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <p className="text-sm font-semibold text-white">Phase 2 — Auto-Draft</p>
                    <p className="text-xs text-slate-400 mt-0.5">AI drafts rate confirmations and negotiates. You approve.</p>
                    <p className="text-[10px] text-purple-300 mt-1 font-medium">Included: Pro, Extreme</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="bg-amber-500/30 text-amber-300 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-1">
                      Phase 3 — Autonomous <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">Fully hands-free: books, matches backhauls, enforces $/mi.</p>
                    <p className="text-[10px] text-amber-300 mt-1 font-medium">Add-On: Extreme + $75/mo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 30-day banner */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="trial-banner">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-2 rounded-full text-sm font-medium">
              <Clock className="w-4 h-4" />
              Pay only the setup fee today — first monthly payment starts in 30 days
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {planOrder.map(key => {
              const plan = plans[key];
              if (!plan) return null;
              const s = PLAN_STYLES[key];
              const Icon = s.icon;

              return (
                <Card
                  key={key}
                  data-testid={`plan-${key}`}
                  className={`${s.border} bg-white rounded-xl hover:scale-[1.02] transition-all relative`}
                >
                  {s.badge && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <span className={`${s.badgeBg} text-white px-4 py-1 rounded-full text-xs font-bold shadow`} data-testid="popular-badge">
                        {s.badge}
                      </span>
                    </div>
                  )}

                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${s.iconColor}`} />
                      <CardTitle className="text-xl text-slate-900">{plan.name}</CardTitle>
                    </div>

                    {/* Setup fee badge */}
                    <div className="mt-3">
                      <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${s.setupBg}`}>
                        ${plan.setup_fee} one-time setup fee
                      </span>
                    </div>

                    {/* Monthly price */}
                    <div className="mt-2">
                      <span className={`text-4xl font-bold ${s.priceColor}`}>${plan.price}</span>
                      <span className="text-slate-500 text-base">/month</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">First payment in 30 days</p>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <ul className="space-y-2.5 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full ${s.btn} text-white font-semibold`}
                      data-testid={`select-${key}-btn`}
                      onClick={() => navigate(`/signup?plan=${key}`)}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Phase 3 AI Add-On */}
          <div className="mt-12" data-testid="phase3-addon-section">
            <Card className="border-2 border-amber-400/60 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent rounded-xl overflow-hidden">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Icon + Title */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="bg-amber-500/20 p-3 rounded-xl">
                      <Bot className="w-7 h-7 text-amber-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-amber-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider">
                          ADD-ON
                        </span>
                        <span className="bg-purple-500/20 text-purple-300 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider">
                          EXTREME PLAN REQUIRED
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                        Phase 3 AI — Autonomous Dispatch
                        <Sparkles className="w-5 h-5 text-amber-400" />
                      </h3>
                      <p className="text-sm text-slate-300 mt-1">
                        Fully autonomous load booking, roundtrip backhaul matching, and fleet rate enforcement — all AI-driven.
                      </p>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="md:text-right md:border-l md:border-white/10 md:pl-6">
                    <div className="flex md:justify-end items-baseline gap-2">
                      <span className="text-3xl font-bold text-amber-400">+$75</span>
                      <span className="text-slate-400 text-sm">/month</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      + $150 one-time activation fee
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Added on top of your Extreme plan
                    </p>
                  </div>
                </div>

                {/* Feature bullets */}
                <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    'Autonomous load booking (no manual clicks)',
                    'AI Roundtrip: outbound + backhaul matching',
                    'Fleet rate rules ($/mile) enforced automatically',
                    'Market intelligence briefings',
                    'Auto-audit log of every AI-booked load',
                    'Priority AI compute lane',
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-200">{f}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <Button
                    className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                    data-testid="phase3-addon-btn"
                    onClick={() => navigate('/signup?plan=extreme&addon=phase3')}
                  >
                    Get Extreme + Phase 3 AI
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-slate-400 text-sm">
              Need a custom plan?{' '}
              <a href="mailto:urbanvisionmag3908@gmail.com" className="text-blue-400 hover:underline">Contact us</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
